
import { useState} from 'react';
import { SelectField } from '../../formFields/SelectField';
import { checkIfFieldIsRequiredPerArchitecture } from '../../../../utilities/architectureTypeDataProcess';
import { getItemByValueInDropdownOptions, isSigningAllowedForModule } from '../../../../utilities/utilityFunctions';
import { clearSigningIdValidationError, isSwSigningIdRequired, validateClusterValue } from '../../../../utilities/formValidation';
import { useDataContext } from '../../../DataProvider/DataContext';
import { textHints } from '../../../../utilities/hintText';
import palette from '../../../../utilities/palette';
import { validSwPartNumber } from '../../../../utilities/regex';
import { FNV23_ARCHITECTURES, FNV3X_ARCHITECTURES, TRADITIONAL_SIGNING_ID } from '../../../../utilities/constants';

export default function ClusterUrlSelector({
        clusterEntry,
        machineList,
        setMachineList, 
        machineIndex, 
        variantIndex, 
        clusterIndex, 
        setMachineListErrors, 
        machineListErrors,
        projectKey,
        moduleName,
    }) {
    
    const {architectureType, api, apiService, signingAllowedModules} = useDataContext();

    const [isLoading, setIsLoading] = useState(false);

    const [error, setError] = useState("");
    
    const handleClusterUrlGeneration = async () => {
        try {
            setError("");
            setIsLoading(true);

            if (shouldFetchButtonBeDisabled()) {
                setIsLoading(false);
                return;
            }
            
            const params = new URLSearchParams({ 
                clusterName: clusterEntry.clusterName,
                supplier: machineList[machineIndex].variantList[variantIndex].supplier,
                moduleName: moduleName,
                projectKey: projectKey,
            });
            const response = await api.fetch(`/srp/form/clusters/urls/options?${params}`);

            if (!response.ok || response.status !== 200) {
                console.warn("Error when generating cluster URL options: ", response.statusText, await response.json());
                setIsLoading(false);
                return;
            }

            let clusterUrlOptions = await response.json();
            if (!Array.isArray(clusterUrlOptions) || clusterUrlOptions.length === 0) {
                console.warn("No cluster URL options found");
                setMachineList((prevState) => {
                    const newState = structuredClone(prevState);
                    newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].clusterUrlOptions = [];
                    return newState;
                });
                setIsLoading(false);
                return;
            }

            clusterUrlOptions = clusterUrlOptions.map(option => {
                return {
                    value: option,
                    label: option.split('/').pop().replace(/\.tar(\.gz)?$/, ''), // remove .tar or .tar.gz from the end of the string
                }
            });

            // IF FNV2 or FNV3, filter out cluster that starts with clusterEntry.clusterName and includes swPartNumber
            if (FNV23_ARCHITECTURES.includes(architectureType)) {
                clusterUrlOptions = clusterUrlOptions.filter(option => (!option.label.startsWith(clusterEntry.clusterName)));
            } else if (FNV3X_ARCHITECTURES.includes(architectureType)) {
                // For FNV3.x, only include cluster that starts with clusterEntry.clusterName
                clusterUrlOptions = clusterUrlOptions.filter(option => option.label.startsWith(clusterEntry.clusterName));
            }

            if (clusterUrlOptions.length === 0) {
                const errorMessage = FNV3X_ARCHITECTURES.includes(architectureType) ?
                    `No cluster container options found starting with ${clusterEntry.clusterName} in repo: ${projectKey}-${machineList[machineIndex].variantList[variantIndex].supplier}${moduleName}-bins-dev-local` :
                    `No cluster container options found starting with ${clusterEntry.swPartNumber} in repo: ${projectKey}-${machineList[machineIndex].variantList[variantIndex].supplier}${moduleName}-bins-dev-local`;
                setError(errorMessage);
            }

            setMachineList((prevState) => {
                const newState = structuredClone(prevState);
                newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].clusterUrlOptions = clusterUrlOptions;
                return newState;
            });
            setIsLoading(false);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            setError(`Unexpected error occurred while populating cluster container options: ${errorMessage}`);
            setIsLoading(false);
            return [];
        }
    }

    return <>
            <div
            style={
                {
                    display: 'flex',
                    flexDirection: 'column',
                }
            }>

            <SelectField
                label="Cluster Container"
                options={clusterEntry.clusterUrlOptions || []}
                required={checkIfFieldIsRequiredPerArchitecture(`machineList.0.variantList.0.clusterList.0.clusterUrl`, architectureType)}
                value={getItemByValueInDropdownOptions(clusterEntry.clusterUrlOptions || [], clusterEntry.clusterUrl)}
                placeholder={shouldFetchButtonBeDisabled() ? "Please select Supplier prior selecting Cluster Container" : "--Please Select--"}
                isLoading={isLoading}
                onFocus={() => {
                    // Fetch options
                    handleClusterUrlGeneration();
                }}
                onChange={(selectedOption) => {
                    handleClusterContainerChange(selectedOption);
                    if (isSigningAllowedForModule(signingAllowedModules, moduleName)) {
                        handlePopulateVBFType(selectedOption);
                    }
                } }
                textHint={textHints.clusterContainer}
                errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.clusterUrlValidationError}
                pathToField={`machineList.0.variantList.0.clusterList.0.clusterUrl`} 
                stylingVariant='body'
            />
            
            <div style={{ color: palette.stepper.error, width: '560px', padding: '5px' }}>
                {error}
            </div>

            </div>


        {/* </Tooltip> */}

    </>;

    function shouldFetchButtonBeDisabled(): boolean {
        return !clusterEntry.clusterName || !machineList[machineIndex].variantList[variantIndex].supplier || !moduleName || !projectKey;
    }

    function extractVersionFromLabel(label: string): string {
        if (!label || !label.includes('_')) {
            return "";
        }

        try {
            const parts = label.split('_');
            const lastPart = parts[parts.length - 1];
            const version = lastPart.split('-')[0];
            
            // Validate semantic version format (x.x.x)
            if (/^\d+\.\d+\.\d+$/.test(version)) {
                return version;
            }
        } catch (error) {
            console.warn(`Failed to extract version from label: ${label}`, error);
        }
        
        return "";
    }

    function extractSwPartNumberFromLabel(label: string): string {
        if (!label || !label.includes('_')) {
            console.warn(`Unable to extract SW Part Number - label does not contain underscore: ${label}`);
            return "";
        }

        try {
            const swPartNumber = label.split('_')[0];
            
            if (!swPartNumber || swPartNumber.trim().length === 0) {
                console.warn(`Extracted empty SW Part Number from label: ${label}`);
                return "";
            }
            
            // Validate against SW Part Number regex pattern
            if (!validSwPartNumber.test(swPartNumber)) {
                console.warn(`Extracted SW Part Number does not match expected format (5-24 chars, format: [prefix-]xxx-xxx-xxx): "${swPartNumber}" from label: ${label}`);
                return "";
            }
            
            return swPartNumber;
        } catch (error) {
            console.warn(`Failed to extract SW Part Number from label: ${label}`, error);
            return "";
        }
    }

    function handleClusterContainerChange(selectedOption) {
        const selectedValue = selectedOption?.value ?? "";
        const selectedLabel = selectedOption?.label ?? "";

        // Extract version from label
        // Example: brk_absu71x_bosch_strgy_allCNR_2.0.8-1762968874-dev.tar.gz
        const extractedVersion = extractVersionFromLabel(selectedLabel);
        const version = extractedVersion || clusterEntry.clusterVersion;

        // Extract swPartNumber from label (first segment before underscore)
        const extractedSwPartNumber = extractSwPartNumberFromLabel(selectedLabel);
        const swPartNumber = extractedSwPartNumber || clusterEntry.swPartNumber;

        // Update machine list state
        setMachineList((prevState) => {
            const newState = structuredClone(prevState);
            const cluster = newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex];
            
            cluster.clusterUrl = selectedValue;
            
            if (FNV3X_ARCHITECTURES.includes(architectureType)) {
                cluster.clusterVersion = version;
                cluster.changeLevel = "NO CHANGE";
            } else if (FNV23_ARCHITECTURES.includes(architectureType)) {
                cluster.swPartNumber = swPartNumber;
            }
            
            return newState;
        });

        // Validate cluster URL
        validateClusterValue(
            machineIndex,
            variantIndex,
            clusterIndex,
            'clusterUrl',
            selectedValue,
            setMachineListErrors
        );

        // Architecture-specific validations
        if (FNV3X_ARCHITECTURES.includes(architectureType)) {
            validateClusterValue(
                machineIndex,
                variantIndex,
                clusterIndex,
                'changeLevel',
                "NO CHANGE",
                setMachineListErrors
            );
            validateClusterValue(
                machineIndex,
                variantIndex,
                clusterIndex,
                'clusterVersion',
                version,
                setMachineListErrors
            );
        } else if (FNV23_ARCHITECTURES.includes(architectureType)) {
            validateClusterValue(
                machineIndex,
                variantIndex,
                clusterIndex,
                'swPartNumber',
                swPartNumber,
                setMachineListErrors
            );
        }
    }

    async function handlePopulateVBFType(selectedOption) {
        /**
         * Flow: 
         * 1. When user selects a cluster container option, extract the cluster URL value
         * 2. Make API call to fetch sign file metadata based on selected cluster URL
         * 3. Extract VBF type and SW Part Number from the metadata response and update machine list state accordingly
         * 4. If VBF type is COMPLEX, set signing ID to empty string and validate signing ID field (which will produce validation error since signing ID is required for COMPLEX VBF type)
         * 5. If VBF type is TRADITIONAL, set signing ID to predefined traditional signing ID value and validate signing ID field (which will pass validation since traditional signing ID is valid for TRADITIONAL VBF type)
         * 6. If any error occurs during this process, set VBF type to empty string, clear signing ID, and set appropriate validation errors to inform user
         */
        const selectedValue = selectedOption?.value ?? "";
        
        // Reset Values and Errors related to VBF type, SW Part Number, sw sign request, and signing ID fields before fetching metadata
        setMachineList((prevState) => {
            const newState = structuredClone(prevState);
            newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].vbfType = "";
            newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].swPartNumber = "";
            newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].swSignRequest = "";
            newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].swSigningId = "";
            newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].isProcessingSignFileMetadata = true;
            return newState;
        });
        setMachineListErrors((prevState) => {
            const newState = structuredClone(prevState);
            newState[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex].vbfTypeValidationError = "";
            newState[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex].swPartNumberValidationError = "";
            newState[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex].swSignRequestValidationError = "";
            newState[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex].swSigningIdValidationError = "";
            return newState;
        });
        // Fetch sign file metadata based on selected cluster URL and update vbfType in machine list state accordingly
        // If error occurs during fetch, set errors in machine list errors state for VBF type, SW Part Number, and Signing ID fields to inform user
        try {
            const response = await apiService.getSignFileMetadata(selectedValue);

            if (!response.isSuccess || !response.data) {
                console.warn(`Failed to fetch sign file metadata for cluster URL: ${selectedValue}`, response.message);
                throw new Error(response.message || "Failed to fetch sign file metadata");
            }

            const metadata = response.data;
            console.log(`Fetched sign file metadata for cluster URL: ${selectedValue}`, metadata);

            let swPartNumber = "";
            let vbfType = "";
            let signingId = "";

            for (const item of metadata) {
                if (item.payloadType === "PRIMARY_SOFTWARE") {
                    swPartNumber = item.softwarePartNumber;
                    vbfType = item.vbfType;
                    break;
                }
            }

            if (swPartNumber === "" || vbfType === "") {
                console.warn(`No item with payloadType "PRIMARY_SOFTWARE" found in metadata for cluster URL: ${selectedValue}`, metadata);
                throw new Error('No item with payloadType "PRIMARY_SOFTWARE" found in metadata');
            }

            if (vbfType === "TRADITIONAL") {
                signingId = TRADITIONAL_SIGNING_ID;
            } else if (vbfType === "COMPLEX") {
                signingId = "";
            }

            setMachineList((prevState) => {
                const newState = structuredClone(prevState);
                newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].swPartNumber = swPartNumber;
                newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].vbfType = vbfType;
                newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].swSigningId = signingId;
                newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].isProcessingSignFileMetadata = false;
                return newState;
            });

            if (isSwSigningIdRequired(clusterEntry.swSignRequest, vbfType)) {
                validateClusterValue(
                    machineIndex,
                    variantIndex,
                    clusterIndex,
                    'swSigningId',
                    signingId,
                    setMachineListErrors,
                );
            } else {
                clearSigningIdValidationError(machineIndex, variantIndex, clusterIndex, setMachineListErrors);
            }

        } catch (error) {
            console.warn(`Failed to fetch sign file metadata for cluster URL: ${selectedValue}`, error);
            setMachineList((prevState) => {
                const newState = structuredClone(prevState);
                newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].vbfType = "";
                newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].swPartNumber = "";
                newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].swSigningId = "";
                newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].swSignRequest = "";
                newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].isProcessingSignFileMetadata = false;
                return newState;
            });
            setMachineListErrors((prevState) => {
                const newState = structuredClone(prevState);
                newState[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex].vbfTypeValidationError = "Unable to extract VBF type. Please verify the container format, Manifest.json entries, and VBF file structure.";
                newState[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex].swPartNumberValidationError = "Unable to extract Software Part Number. Please verify the container format, Manifest.json entries, and VBF file structure.";
                newState[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex].swSigningIdValidationError = "";
                newState[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex].swSignRequestValidationError = "";
                return newState;
            });
        }
    }
}
